/**
 * Offline draft persistence for the Comprehensive Intake form.
 *
 * Why this exists:
 *   - The intake is 8 sections / 15-20 minutes long. Without persistence,
 *     a refresh / lost tab / dead battery / lost signal wipes everything
 *     and the patient gives up.
 *   - Patients in waiting rooms, on poor cell signal, or simply hesitant
 *     to commit to a login up-front need to be able to START the survey
 *     and have their answers held safely on their device.
 *
 * Behavior:
 *   - Auto-save formData + currentSection to localStorage on every change.
 *   - Drafts auto-expire after 48 hours so we never load stale answers
 *     into a fresh visit.
 *   - One key per "draft slot" (currently just 'guest') so we could later
 *     keep an authenticated user's draft separate from a guest draft.
 */

const STORAGE_KEY = 'cadence_intake_draft_v1';
const TTL_MS = 48 * 60 * 60 * 1000; // 48 hours

export const saveDraft = (formData, currentSection) => {
  try {
    const payload = {
      formData,
      currentSection,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage full / disabled — fail silently. Draft just won't persist.
  }
};

export const loadDraft = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // 48-hour TTL — older drafts are silently discarded.
    if (Date.now() - (parsed.savedAt || 0) > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearDraft = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Pending submission queue — used when a guest hits Submit while offline.
 * We stash the submission payload locally and retry it on the next 'online'
 * event (or on next mount if the user re-opens the app online).
 */
const PENDING_SUBMIT_KEY = 'cadence_intake_pending_submit_v1';

export const queuePendingSubmit = (payload) => {
  try {
    localStorage.setItem(PENDING_SUBMIT_KEY, JSON.stringify({
      payload,
      queuedAt: Date.now(),
    }));
  } catch { /* ignore */ }
};

export const loadPendingSubmit = () => {
  try {
    const raw = localStorage.getItem(PENDING_SUBMIT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.payload ? parsed : null;
  } catch {
    return null;
  }
};

export const clearPendingSubmit = () => {
  try {
    localStorage.removeItem(PENDING_SUBMIT_KEY);
  } catch { /* ignore */ }
};
